/**
 * useShellLedger - the modifier ledger (v8.222.0): the pre-roll truth.
 *
 * Every modifier the next attack will roll with, as a CHIP (label, value,
 * where it came from, and how to stand it down when it can be), plus their
 * total and the skill breakdown the attack panel shows before the dice. The
 * chips are the same list the turn card and the probability plots read, so
 * none of them can show a modifier the roll does not use.
 *
 * Returns `{ chips, total, breakdown }` - empty with no weapon armed.
 * Extracted from DeathWatchSimulator; the chips and their order are unchanged.
 */
import { useMemo } from 'react';
import { hasWS } from '../engine/weaponSpecials';
import { computeToggleEntries } from '../engine/actions/toggleEntries';
import { getAttackConditionModifiers } from '../engine/conditionModifiers';
import { calcSkillBreakdown, RANGE_MODS, RANGE_BAND_LABELS } from '../engine/attackCalc';

/** The stance toggles (aim, charge, all-out...), each removable by lowering it. */
function stanceChips(a) {
  const specialRules = a.effectiveSlot?.specialRules || [];
  const entries = computeToggleEntries(
    a.attackToggles,
    a.calledShotReduction,
    a.hasBerserkCharge,
    a.isMelee,
    hasWS(specialRules, 'Inaccurate'),
    specialRules,
    a.targetNoAim,
  );
  return entries.map((e) => ({
    id: `toggle-${e.key}`,
    label: e.label,
    value: e.value,
    source: 'stance',
    onRemove: () => a.setAttackToggles({ ...a.attackToggles, [e.key]: false }),
  }));
}

/** Hatred and Frenzied: melee only. Frenzy is a state of mind, not a chip you lower. */
function qualityChips({ hatred, frenzied, isMelee, setHatred }) {
  const chips = [];
  if (hatred && isMelee)
    chips.push({ id: 'hatred', label: 'Hatred', value: 10, source: 'quality', onRemove: () => setHatred(false) });
  if (frenzied && isMelee)
    chips.push({ id: 'frenzied', label: 'Frenzied', value: 10, source: 'quality', onRemove: null });
  return chips;
}

/**
 * The custom modifiers. A derived mod is a fact about the action, not a chip
 * the user owns - there is nothing to stand down but the action itself.
 */
function customChips(mods, setAttackCustomMods) {
  return mods.map((m) => ({
    id: m.id,
    label: m.label,
    value: m.value,
    source: 'custom',
    onRemove: m.derived ? null : () => setAttackCustomMods((all) => all.filter((x) => x.id !== m.id)),
  }));
}

/** The conditions on both sides that move the to-hit, when the table applies them. */
function conditionEntries(a) {
  if (!a.applyConditionModifiers) return [];
  return getAttackConditionModifiers(
    a.actorProjection ?? { conditions: [] },
    a.currentTab?.target ?? { conditions: [] },
    a.isMelee,
    a.condModOptions,
    { charge: !!a.attackToggles.charge, rangeBand: a.rangeBand },
  ).entries;
}

/** The range band's chip, for a ranged attack at anything but Normal. */
function rangeChips(isMelee, rangeBand) {
  if (isMelee || rangeBand === 'normal' || RANGE_MODS[rangeBand] === undefined) return [];
  return [
    {
      id: 'range',
      label: RANGE_BAND_LABELS[rangeBand] ?? rangeBand,
      value: RANGE_MODS[rangeBand],
      source: 'range',
      onRemove: null,
    },
  ];
}

/** The pre-roll skill breakdown: the same inputs the roll resolves with. */
function breakdownOf(a, condEntries) {
  const t = a.attackToggles;
  return calcSkillBreakdown({
    slot: a.effectiveSlot ?? a.slot,
    attackMod: a.attackModFinal + condEntries.reduce((sum, e) => sum + e.value, 0),
    customModifiers: [...condEntries, ...a.effectiveCustomMods],
    targetSize: a.currentTab?.target?.size ?? 'Average',
    rangeBand: a.rangeBand,
    isMelee: a.isMelee,
    hatred: a.hatred,
    frenzied: a.frenzied,
    removeLongRangePenalty: a.attackerCharStats?.removeLongRangePenalty ?? false,
    removeExtremeRangePenalty: a.attackerCharStats?.removeExtremeRangePenalty ?? false,
    isAimHalf: t.aimHalf,
    isAimFull: t.aimFull,
    isAllOutAttack: t.allOutAttack,
    isGuardedAttack: t.guardedAttack,
    isCharge: t.charge,
    isGangUp: t.gangUp,
    hasBerserkCharge: a.hasBerserkCharge,
    isCalledShot: t.calledShot,
    calledShotReduction: a.calledShotReduction,
    calledShotExposure: a.calledShotExposure,
    calledShotIgnored: a.ignoreCalledShot,
  });
}

/** The whole ledger, in chip order: stance, quality, custom, condition, range. */
export function buildLedger(a) {
  if (!a.slot || a.slot.isEmpty) return { chips: [], total: 0, breakdown: [] };
  const condEntries = conditionEntries(a);
  const chips = [
    ...stanceChips(a),
    ...qualityChips(a),
    ...customChips(a.effectiveCustomMods, a.setAttackCustomMods),
    ...condEntries.map((e, i) => ({
      id: `cond-${i}`,
      label: e.label,
      value: e.value,
      source: 'condition',
      onRemove: null,
    })),
    ...rangeChips(a.isMelee, a.rangeBand),
  ];
  const total = chips.reduce((sum, c) => sum + c.value, 0);
  return { chips, total, breakdown: breakdownOf(a, condEntries) };
}

/**
 * @param {object} args - the armed slot and its overridden view, the attack
 *   toggles and their setter, the custom mods and theirs, setHatred, the
 *   qualities, the called shot's reduction and exposure, the condition
 *   options, the actor's projection, the current tab, the range band, the
 *   final attack modifier and the attacker's resolved stats.
 *
 * The memo depends on everything it reads; it once omitted
 * `calledShotExposure` and `setAttackCustomMods` and served a stale ledger
 * after either changed (v8.377.3). `setHatred` is a state setter and so
 * stable; listing it changes nothing.
 *
 * Over the length target on purpose: an input list and a dependency list,
 * both declarative.
 */
export default function useShellLedger(args) {
  const { slot, effectiveSlot, attackToggles, setAttackToggles, setHatred, calledShotReduction } = args;
  const { hasBerserkCharge, isMelee, hatred, frenzied, effectiveCustomMods, applyConditionModifiers } = args;
  const { condModOptions, actorProjection, currentTab, rangeBand, attackModFinal, attackerCharStats } = args;
  const { targetNoAim, calledShotExposure, ignoreCalledShot, setAttackCustomMods } = args;
  return useMemo(
    () =>
      buildLedger({
        slot,
        effectiveSlot,
        attackToggles,
        setAttackToggles,
        setHatred,
        calledShotReduction,
        hasBerserkCharge,
        isMelee,
        hatred,
        frenzied,
        effectiveCustomMods,
        applyConditionModifiers,
        condModOptions,
        actorProjection,
        currentTab,
        rangeBand,
        attackModFinal,
        attackerCharStats,
        targetNoAim,
        calledShotExposure,
        ignoreCalledShot,
        setAttackCustomMods,
      }),
    [
      calledShotExposure,
      ignoreCalledShot,
      setAttackCustomMods,
      slot,
      effectiveSlot,
      attackToggles,
      setAttackToggles,
      setHatred,
      calledShotReduction,
      hasBerserkCharge,
      isMelee,
      hatred,
      frenzied,
      effectiveCustomMods,
      applyConditionModifiers,
      condModOptions,
      actorProjection,
      currentTab,
      rangeBand,
      attackModFinal,
      attackerCharStats,
      targetNoAim,
    ],
  );
}
